import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, ShieldCheck, Crown, Eye, EyeOff, Pause, Play } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadges } from "@/components/VerifiedBadges";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

export default function MyProfile() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/profile/me").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  if (!data) return <FullSpinner />;
  const p = data.profile;

  const setVis = async (mode) => { await api.put("/profile/visibility", { mode }); toast.success(`Profile is now ${mode}`); load(); refresh(); };
  const vis = p.visibility || "active";

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="my-profile-page">
      <div className="mj-card overflow-hidden">
        <div className="h-24 bg-[#3B0A12]" />
        <div className="px-5 pb-5">
          <div className="flex items-end justify-between -mt-10">
            <Avatar photos={p.photos} name={p.full_name} size={80} className="rounded-2xl border-4 border-white" />
            <Link to="/app/profile/edit" className="btn-outline text-sm" data-testid="edit-profile-btn"><Pencil className="w-4 h-4" /> Edit</Link>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-[#261A1C]">{p.full_name}{p.age ? `, ${p.age}` : ""}</h1>
              <p className="text-sm text-[#605255]">{[p.city, p.occupation].filter(Boolean).join(" · ")} · <span className="font-mono text-[#9E7B1B]">{p.profile_code}</span></p>
            </div>
            <span className="chip"><Crown className="w-3.5 h-3.5" /> {user.subscription.lifetime ? "Lifetime" : user.subscription.plan === "premium" ? "Premium" : "Free"}</span>
          </div>
          <div className="mt-4"><VerifiedBadges verification={p.verification} /></div>
        </div>
      </div>

      <div className="mj-card p-5">
        <div className="flex items-center justify-between mb-2"><h2 className="font-serif text-lg font-semibold text-[#261A1C]">Profile Completion</h2><span className="font-serif text-xl font-bold text-[#781826]">{p.completion}%</span></div>
        <div className="h-2.5 rounded-full bg-[#F1E9DA] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.completion}%`, background: "linear-gradient(90deg,#C59B27,#781826)" }} /></div>
        {data.tips?.length > 0 && <ul className="mt-3 space-y-1">{data.tips.map((t) => <li key={t} className="text-sm text-[#605255]">• {t}</li>)}</ul>}
      </div>

      <div className="mj-card p-5" data-testid="visibility-section">
        <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-1">Profile Visibility</h2>
        <p className="text-xs text-[#605255] mb-3">Current status: <span className="font-semibold text-[#781826] capitalize">{vis}</span></p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["active", "Active", Play], ["paused", "Pause", Pause], ["hidden", "Hidden", EyeOff], ["private", "Private", Eye]].map(([m, l, Icon]) => (
            <button key={m} onClick={() => setVis(m)} data-testid={`vis-${m}`} className={`text-sm py-2.5 rounded-xl border flex flex-col items-center gap-1 ${vis === m ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        <p className="text-xs text-[#9E7B1B] mt-3">Paused/Hidden profiles won't appear in discovery. Existing conversations remain accessible.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/app/preferences" className="mj-card p-4 hover:border-[#C59B27]/50">Partner Preferences →</Link>
        <Link to="/app/compatibility-lab" className="mj-card p-4 hover:border-[#C59B27]/50">Compatibility Lab →</Link>
        <Link to="/app/verification" className="mj-card p-4 hover:border-[#C59B27]/50">Trust & Verification →</Link>
        <Link to="/app/settings" className="mj-card p-4 hover:border-[#C59B27]/50">Privacy & Settings →</Link>
      </div>
    </div>
  );
}
